import { useState, useRef, useEffect, useMemo } from "react";
import OutsideClickHandler from "react-outside-click-handler";
import classes from "./tags.module.css";
import { toTitleCase } from "../../../utils/functions";
import styles from "./tags.module.css";
import { IconX } from "@tabler/icons-react";


const TagsInput = ({
  data,
  setData,
  defaultData,
  style,
  placeholder,
  required = false,
  maxTags,
}) => {
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const handleSubmit = (e) => {
    e.preventDefault();
    // const newArray = [...data];
    // newArray.push(value);
    // setData(newArray);
    // setValue("");
    const trimmed = value.trim();
    if (!trimmed) return;
    if (data.some((item) => item.toLowerCase() === trimmed.toLowerCase()))
      return; // Prevent duplicates
    addToArray(trimmed); // If user typed a skill not in the list, add it
  };

  const [active, setActive] = useState(false);

  const [languages, setLanguages] = useState(defaultData);
  const addToArray = (inputVal) => {
    const newArray = [...data];
    newArray.push(inputVal);
    setData(newArray);
    const newLang = [...languages];
    const filteredLang = newLang.filter((item) => item !== inputVal);
    setLanguages(filteredLang);
    setValue("");
    inputRef.current.focus();
  };

  const removeFromArray = (removedVal) => {
    const removed = [...data];
    removed.splice(removed.indexOf(removedVal), 1);
    setData(removed);
    const newLang = [...languages];
    newLang.push(removedVal);
    setLanguages(newLang);
  };

  const disableInput = useMemo(() => {
    return data?.length >= maxTags;
  }, [data, maxTags]);

  useEffect(() => {
    if (disableInput) {
      setActive(false);
    }
  }, [disableInput]);

  return (
    <div className={`relative ${classes.container}`}>
      <div className={classes["tags-container"]}>
        {data.map((item, index) => (
          <div
            key={`item-${index}`}
            className={classes.tags}
            onClick={() => removeFromArray(item)}
          >
            <span>{item}</span>
            &#88;
          </div>
        ))}
        <form onSubmit={handleSubmit}>
          <input
            required={required}
            type="text"
            ref={inputRef}
            placeholder={
              disableInput
                ? `Maximum ${maxTags} tags reached`
                : placeholder || "Add a skill"
            }
            disabled={disableInput}
            className={classes.input}
            value={value}
            onFocus={() => setActive(true)}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" disabled={disableInput}></button>
        </form>
      </div>
      {active && (
        <OutsideClickHandler onOutsideClick={() => setActive(false)}>
          <div className={classes["options-container"]} style={style}>
            {languages
              .filter((item) =>
                item.toLowerCase().includes(value.toLowerCase()),
              )
              .map((item, index) => (
                <div
                  className={classes.options}
                  onClick={() => addToArray(item)}
                  key={`option-${index}`}
                >
                  {item}
                </div>
              ))}
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

export default TagsInput;

/**
 * TagSearchInput — Reusable multi-select tag input with live autocomplete.
 * Used in Onboarding Step 2 and ClassificationCard (company profile).
 *
 * Props:
 *   selected     {string[]}              Currently selected tags
 *   onChange     {(tags: string[]) => void}  Called when selection changes
 *   suggestions  {string[]}              Full list of autocomplete options
 *   placeholder  {string}               Input placeholder text
 *   hasError     {boolean}              Adds red border when true
 */




export const TagSearchInput = ({
  selected = [],
  onChange,
  suggestions = [],
  placeholder = "Type to search or add…",
  hasError = false,
}) => {
  const [query, setQuery] = useState("");
  const [focusIdx, setFocusIdx] = useState(-1);
  const inputRef = useRef(null);

  // Compute dropdown items
  const q = query.trim().toLowerCase();
  const matches = q
    ? suggestions.filter(
        (s) =>
          s.toLowerCase().includes(q) && !selected.includes(toTitleCase(s)),
      )
    : [];
  const exactMatch = suggestions.some((s) => s.toLowerCase() === q);
  const alreadySel = selected.includes(toTitleCase(query.trim()));
  const showNew = q && !exactMatch && !alreadySel;
  const dropdownItems = [...matches, ...(showNew ? ["__new__"] : [])];
  const showDropdown = dropdownItems.length > 0;

  const addTag = (name) => {
    const val = toTitleCase(name);
    if (!val || selected.includes(val)) return;
    onChange([...selected, val]);
    setQuery("");
    setFocusIdx(-1);
    inputRef.current?.focus();
  };

  const removeTag = (name) => {
    onChange(selected.filter((s) => s !== name));
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx((i) => Math.min(i + 1, dropdownItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (focusIdx >= 0 && dropdownItems[focusIdx]) {
        const item = dropdownItems[focusIdx];
        addTag(item === "__new__" ? query.trim() : item);
      } else if (query.trim()) {
        addTag(query.trim());
      }
    } else if (e.key === "Escape") {
      setQuery("");
      setFocusIdx(-1);
    } else if (e.key === "Backspace" && query === "" && selected.length > 0) {
      removeTag(selected[selected.length - 1]);
    }
  };

  return (
    <div className={styles.wrap}>
      {/* Input row with selected tags */}
      <div
        className={`${styles.inputRow} ${hasError ? styles.inputRowErr : ""}`}
        onClick={() => inputRef.current?.focus()}
      >
        {selected.map((name) => (
          <span key={name} className={styles.tagEditable}>
            {name}
            <button
              className={styles.tagRemove}
              aria-label={`Remove ${name}`}
              onClick={(e) => {
                e.stopPropagation();
                removeTag(name);
              }}
            >
              <IconX size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className={styles.searchInput}
          placeholder={selected.length === 0 ? placeholder : ""}
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setFocusIdx(-1);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() =>
            setTimeout(() => {
              setQuery("");
              setFocusIdx(-1);
            }, 150)
          }
        />
      </div>

      {/* Autocomplete dropdown */}
      {showDropdown && (
        <div className={styles.dropdown}>
          {dropdownItems.map((item, i) => {
            if (item === "__new__") {
              return (
                <div
                  key="__new__"
                  className={`${styles.acItem} ${styles.acItemNew} ${i === focusIdx ? styles.acItemNewFocused : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(query.trim());
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `+ Add "<mark>${toTitleCase(query.trim())}</mark>"`,
                  }}
                />
              );
            }
            const idx = item.toLowerCase().indexOf(q);
            return (
              <div
                key={item}
                className={`${styles.acItem} ${i === focusIdx ? styles.acItemFocused : ""}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(item);
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    item.slice(0, idx) +
                    `<mark>${item.slice(idx, idx + q.length)}</mark>` +
                    item.slice(idx + q.length),
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
